"use client";
import {
  Profiel,
  ProfielEditie,
  ProfielJaar,
  ProfielMethode,
  ProfielNiveau,
  ProfielWiskundeType
} from "@/lib/profiel_types";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useAppContext } from "./AppContext";
import { useAuthContext } from "./AuthContext";
import { Hoofdstuk } from "@/lib/types";

export type ContentSelectiePhase = "profiel" | "modus";

type ContentSelectieContext = {
  profiel: Profiel | undefined | null;
  setProfiel: Dispatch<SetStateAction<Profiel | undefined | null>>;
  removeProfiel: () => void;
  startProfielEdit: () => void;

  selectedMethode: ProfielMethode | undefined;
  setSelectedMethode: Dispatch<SetStateAction<ProfielMethode | undefined>>;

  selectedEdition: ProfielEditie | undefined;
  setSelectedEdition: Dispatch<SetStateAction<ProfielEditie | undefined>>;

  selectedJaar: ProfielJaar | undefined;
  setSelectedJaar: Dispatch<SetStateAction<ProfielJaar | undefined>>;

  selectedNiveau: ProfielNiveau | undefined;
  setSelectedNiveau: Dispatch<SetStateAction<ProfielNiveau | undefined>>;

  selectedWiskundeType: ProfielWiskundeType | undefined;
  setSelectedWiskundeType: Dispatch<
    SetStateAction<ProfielWiskundeType | undefined>
  >;

  selectedHoofdstuk: number | undefined;
  setSelectedHoofdstuk: Dispatch<SetStateAction<number | undefined>>;

  phase: ContentSelectiePhase;

  currentHoofdstuk: Hoofdstuk | undefined;
  loadHoofdstuk: (id: number) => Promise<Hoofdstuk>;
  deselectHoofdstuk: () => void;
};

const ContentSelectieContext = createContext<ContentSelectieContext | null>(
  null
);

export const useContentSelectieContext = () => {
  const context = useContext(ContentSelectieContext);
  if (context === null) {
    throw Error("No ContentSelectieContext available");
  }
  return context;
};

export const ContentSelectieContextProvider = ({
  children
}: PropsWithChildren) => {
  const { api, setHoofdstukken } = useAppContext();
  const { userData, isPremium } = useAuthContext();

  const skipSaveRef = useRef(false);
  const profileEditRestoreRef = useRef<Profiel | null>(null);
  const isEditingProfileRef = useRef(false);

  // Final state
  const [profiel, setProfiel] = useState<Profiel | undefined | null>(null);
  const [currentHoofdstuk, setCurrentHoofdstuk] = useState<Hoofdstuk>();

  // Menu state, not the final state
  const [selectedMethode, setSelectedMethode] = useState<ProfielMethode>();
  const [selectedEdition, setSelectedEdition] = useState<ProfielEditie>();
  const [selectedJaar, setSelectedJaar] = useState<ProfielJaar>();
  const [selectedNiveau, setSelectedNiveau] = useState<ProfielNiveau>();
  const [selectedWiskundeType, setSelectedWiskundeType] =
    useState<ProfielWiskundeType>();
  const [selectedHoofdstuk, setSelectedHoofdstuk] = useState<number>();

  const [phase, setPhase] = useState<ContentSelectiePhase>("profiel");

  // Derive phase from profiel and isPremium
  useEffect(() => {
    if (profiel?.hoofdstukId !== undefined && isPremium) {
      setPhase("modus");
    } else {
      setPhase("profiel");
    }
  }, [profiel, isPremium]);

  // Change to another profiel
  const removeProfiel = useCallback(async () => {
    setSelectedJaar(undefined);
    setSelectedMethode(undefined);
    setSelectedNiveau(undefined);
    setSelectedEdition(undefined);
    setSelectedWiskundeType(undefined);
    setSelectedHoofdstuk(undefined);
    setProfiel(undefined);
    if (userData) {
      await api.deleteProfiel().catch(() => {});
    }
  }, [api, setProfiel, userData]);

  const restoreProfileSelection = useCallback((profile: Profiel) => {
    setSelectedMethode(profile.methode);
    setSelectedEdition(profile.editie);
    setSelectedJaar(profile.jaar);
    setSelectedNiveau(profile.niveau);
    setSelectedWiskundeType(profile.wiskundeType);
    setSelectedHoofdstuk(profile.hoofdstukId);
    setProfiel(profile);
  }, []);

  const startProfielEdit = useCallback(() => {
    if (!profiel) return;

    profileEditRestoreRef.current = profiel;
    isEditingProfileRef.current = true;

    window.history.pushState(
      { wiswizProfileEdit: true },
      "",
      window.location.href
    );

    setSelectedJaar(undefined);
    setSelectedMethode(undefined);
    setSelectedNiveau(undefined);
    setSelectedEdition(undefined);
    setSelectedWiskundeType(undefined);
    setSelectedHoofdstuk(undefined);
    setProfiel(undefined);
    setCurrentHoofdstuk(undefined);
  }, [profiel]);

  useEffect(() => {
    const handlePopState = () => {
      const profileToRestore = profileEditRestoreRef.current;
      if (!profileToRestore) return;

      profileEditRestoreRef.current = null;
      isEditingProfileRef.current = false;
      restoreProfileSelection(profileToRestore);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [restoreProfileSelection]);

  // Sync profiel from server whenever userData changes (login, initial load, logout)
  const prevServerProfielRef = useRef(userData?.profiel);
  useEffect(() => {
    if (!userData) {
      prevServerProfielRef.current = undefined;
      skipSaveRef.current = true;
      setProfiel(undefined);
      setSelectedMethode(undefined);
      setSelectedEdition(undefined);
      setSelectedJaar(undefined);
      setSelectedNiveau(undefined);
      setSelectedWiskundeType(undefined);
      setSelectedHoofdstuk(undefined);
      setCurrentHoofdstuk(undefined);
      return;
    }
    // Skip if only unrelated parts of userData changed (e.g., huiswerk_set)
    if (prevServerProfielRef.current === userData.profiel) return;
    prevServerProfielRef.current = userData.profiel;

    if (userData.profiel) {
      const p = userData.profiel;
      skipSaveRef.current = true;
      const restoredProfiel = {
        methode: p.methode as ProfielMethode,
        editie: p.editie as ProfielEditie,
        jaar: p.jaar as ProfielJaar,
        niveau: p.niveau as ProfielNiveau,
        wiskundeType: p.wiskunde_type as ProfielWiskundeType | undefined,
        hoofdstukId: p.hoofdstuk_id
      };
      setProfiel(restoredProfiel);
      setSelectedMethode(restoredProfiel.methode);
      setSelectedEdition(restoredProfiel.editie);
      setSelectedJaar(restoredProfiel.jaar);
      setSelectedNiveau(restoredProfiel.niveau);
      setSelectedWiskundeType(restoredProfiel.wiskundeType);
      setSelectedHoofdstuk(restoredProfiel.hoofdstukId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Trigger when profiel-selection is done. Set the Profiel.
  useEffect(() => {
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    if (selectedJaar && selectedMethode && selectedNiveau && selectedEdition) {
      const newProfiel = {
        methode: selectedMethode,
        editie: selectedEdition,
        jaar: selectedJaar,
        niveau: selectedNiveau,
        wiskundeType: selectedWiskundeType,
        hoofdstukId: selectedHoofdstuk
      };

      setProfiel(newProfiel);
      if (userData) {
        api.postProfiel(newProfiel).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedJaar,
    selectedMethode,
    selectedNiveau,
    selectedEdition,
    selectedHoofdstuk,
    selectedWiskundeType,
    api,
    setProfiel
  ]);

  useEffect(() => {
    // When a profiel & api are available, get the hoofdstukken list
    let cancelled = false;

    const getH = async () => {
      if (!profiel) return;

      try {
        const result = await api.getHoofdstukkenList(profiel);
        if (!cancelled) setHoofdstukken(result.hoofdstukken);
      } catch (error) {
        console.error("Failed to load hoofdstukken list", error);
        if (!cancelled) setHoofdstukken([]);
      }
    };

    void getH();

    return () => {
      cancelled = true;
    };
  }, [profiel, setHoofdstukken, api]);

  useEffect(() => {
    if (
      isEditingProfileRef.current &&
      profileEditRestoreRef.current &&
      profiel &&
      profiel !== profileEditRestoreRef.current &&
      profiel.hoofdstukId !== undefined
    ) {
      profileEditRestoreRef.current = null;
      isEditingProfileRef.current = false;
    }
  }, [profiel]);

  const currentHoofdstukRef = useRef(currentHoofdstuk);
  currentHoofdstukRef.current = currentHoofdstuk;

  const loadHoofdstuk = useCallback(
    async (id: number) => {
      if (currentHoofdstukRef.current?.id === id)
        return currentHoofdstukRef.current;
      const data = await api.getHoofdstuk(id);
      setCurrentHoofdstuk(data);
      return data;
    },
    [api, setCurrentHoofdstuk]
  );

  const hoofdstukId = profiel?.hoofdstukId;
  useEffect(() => {
    let cancelled = false;

    if (hoofdstukId !== undefined) {
      loadHoofdstuk(hoofdstukId).catch((error) => {
        console.error("Failed to load hoofdstuk", error);
        if (!cancelled) setCurrentHoofdstuk(undefined);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [hoofdstukId, loadHoofdstuk]);

  const deselectHoofdstuk = useCallback(() => {
    setProfiel((prev) => {
      if (prev) {
        const updated = { ...prev, hoofdstukId: undefined };
        if (userData) {
          api.postProfiel(updated).catch(() => {});
        }
        return updated;
      }
      return prev;
    });
    skipSaveRef.current = true;
    setSelectedHoofdstuk(undefined);
    setCurrentHoofdstuk(undefined);
  }, [api, userData]);

  const providerValue = useMemo(
    () => ({
      profiel,
      setProfiel,
      removeProfiel,
      startProfielEdit,
      selectedMethode,
      setSelectedMethode,
      selectedEdition,
      setSelectedEdition,
      selectedJaar,
      setSelectedJaar,
      selectedNiveau,
      setSelectedNiveau,
      selectedWiskundeType,
      setSelectedWiskundeType,
      selectedHoofdstuk,
      setSelectedHoofdstuk,
      phase,
      deselectHoofdstuk,
      currentHoofdstuk,
      loadHoofdstuk
    }),
    [
      profiel,
      selectedMethode,
      selectedEdition,
      selectedJaar,
      selectedNiveau,
      selectedWiskundeType,
      selectedHoofdstuk,
      phase,
      currentHoofdstuk,
      removeProfiel,
      startProfielEdit,
      deselectHoofdstuk,
      loadHoofdstuk
    ]
  );

  return (
    <ContentSelectieContext.Provider value={providerValue}>
      {children}
    </ContentSelectieContext.Provider>
  );
};
